/**
 * EPH Connection Manager
 * A utility to ensure Chrome extension connection before executing tasks
 * 
 * Usage in React app:
 * import { ensureConnectionAndExecute, EPHConnectionManager } from './eph-connection-manager';
 * 
 * // For simple tasks
 * await ensureConnectionAndExecute('scrape_profile', { profileUrl: 'https://...' });
 * 
 * // For more control
 * const manager = new EPHConnectionManager();
 * await manager.executeWithConnection('send_inmail', { profileUrl: '...', message: '...' });
 */

class EPHConnectionManager {
    constructor() {
        this.isConnected = false;
        this.connectionPromises = new Map();
        this.listenersSetup = false;
        this.lastConnectionCheck = 0;
        this.connectionCheckInterval = null;
        this.setupEventListeners();
        this.startPeriodicConnectionCheck();
    }

    setupEventListeners() {
        if (this.listenersSetup) {
            console.log('🔍 [EPH Manager] Event listeners already set up, skipping');
            return;
        }
        
        console.log('🔍 [EPH Manager] Setting up event listeners');
        // Listen for connection status updates from extension
        window.addEventListener('message', (event) => {
            if (event.source !== window) return;

            // Log all messages to debug
            if (event.data?.type && event.data.type.startsWith('EPH_')) {
                console.log('🔍 [EPH Manager] Received message:', event.data.type, event.data);
            }

            switch (event.data?.type) {
                case 'EPH_CONNECTION_READY':
                    console.log('🔍 [EPH Manager] Processing EPH_CONNECTION_READY');
                    this.handleConnectionReady(event.data);
                    break;
                case 'EPH_LOGIN_REQUIRED':
                    console.log('🔍 [EPH Manager] Processing EPH_LOGIN_REQUIRED');
                    this.handleLoginRequired(event.data);
                    break;
                case 'EPH_NO_TOKEN':
                    console.log('🔍 [EPH Manager] Processing EPH_NO_TOKEN');
                    this.handleNoToken(event.data);
                    break;
                case 'EPH_EXTENSION_INVALID':
                    console.log('🔍 [EPH Manager] Processing EPH_EXTENSION_INVALID');
                    this.handleExtensionInvalid(event.data);
                    break;
            }
        });
        
        this.listenersSetup = true;
        console.log('🔍 [EPH Manager] Event listeners set up successfully');
    }

    handleConnectionReady(data) {
        console.log('✅ [EPH Manager] Connection ready for task:', data.taskType);
        console.log('✅ [EPH Manager] Full data received:', data);
        console.log('✅ [EPH Manager] Current connectionPromises:', this.connectionPromises);
        this.isConnected = true;
        this.lastConnectionCheck = Date.now();
        
        // Resolve any pending promises for this task type
        const promise = this.connectionPromises.get(data.taskType);
        console.log('✅ [EPH Manager] Found promise for taskType:', !!promise);
        if (promise) {
            console.log('✅ [EPH Manager] Resolving promise for:', data.taskType);
            promise.resolve({ connected: true, message: data.message });
            this.connectionPromises.delete(data.taskType);
            console.log('✅ [EPH Manager] Promise resolved and deleted');
        } else {
            console.warn('⚠️ [EPH Manager] No promise found for taskType:', data.taskType);
            console.warn('⚠️ [EPH Manager] Available taskTypes in promises:', Array.from(this.connectionPromises.keys()));
        }
    }

    handleLoginRequired(data) {
        console.warn('⚠️ [EPH Manager] Login required for task:', data.taskType);
        
        // Resolve promise with login required status
        const promise = this.connectionPromises.get(data.taskType);
        if (promise) {
            promise.resolve({ 
                connected: false, 
                loginRequired: true, 
                message: data.message 
            });
            this.connectionPromises.delete(data.taskType);
        }
    }

    handleNoToken(data) {
        console.warn('⚠️ [EPH Manager] No token available for task:', data.taskType);
        
        const promise = this.connectionPromises.get(data.taskType);
        if (promise) {
            promise.resolve({ 
                connected: false, 
                noToken: true, 
                message: data.message 
            });
            this.connectionPromises.delete(data.taskType);
        }
    }

    handleExtensionInvalid(data) {
        console.error('❌ [EPH Manager] Extension context invalidated for task:', data.taskType);
        
        const promise = this.connectionPromises.get(data.taskType);
        if (promise) {
            promise.reject(new Error(`Extension context invalidated: ${data.message}`));
            this.connectionPromises.delete(data.taskType);
        } else {
            console.warn('⚠️ [EPH Manager] No promise found for invalidated extension task:', data.taskType);
        }
    }

    /**
     * Ensure connection before executing a task
     * @param {string} taskType - Type of task to execute
     * @param {number} timeout - Timeout in milliseconds (default: 10000)
     * @returns {Promise<object>} Connection status
     */
    async ensureConnection(taskType, timeout = 10000) {
        return new Promise((resolve, reject) => {
            console.log(`🔗 [EPH Manager] Starting ensureConnection for: ${taskType}`);
            console.log(`🔗 [EPH Manager] Listeners setup status: ${this.listenersSetup}`);

            // Ensure event listeners are set up
            if (!this.listenersSetup) {
                console.log(`🔗 [EPH Manager] Setting up listeners for: ${taskType}`);
                this.setupEventListeners();
            }
            
            // Store the promise for this task
            console.log(`🔗 [EPH Manager] Storing promise for: ${taskType}`);
            this.connectionPromises.set(taskType, { resolve, reject });
            console.log(`🔗 [EPH Manager] Current promises:`, Array.from(this.connectionPromises.keys()));

            // Request connection verification from extension
            console.log(`🔗 [EPH Manager] Requesting connection for task: ${taskType}`);
            console.log(`🔗 [EPH Manager] Dispatching eph-ensure-connection event`);
            window.dispatchEvent(new CustomEvent('eph-ensure-connection', {
                detail: { taskType }
            }));

            // Set timeout
            setTimeout(() => {
                if (this.connectionPromises.has(taskType)) {
                    console.warn(`⏰ [EPH Manager] Connection timeout for task: ${taskType}`);
                    this.connectionPromises.delete(taskType);
                    reject(new Error(`Connection timeout for task: ${taskType}`));
                }
            }, timeout);
        });
    }

    /**
     * Execute a task with guaranteed connection
     * @param {string} taskType - Type of task
     * @param {function} taskFunction - Function to execute after connection (optional)
     * @returns {Promise<any>} Task result
     */
    async executeWithConnection(taskType, taskFunction = null, retries = 2) {
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                console.log(`🚀 [EPH Manager] Starting task with connection: ${taskType} (attempt ${attempt + 1}/${retries + 1})`);
                
                // First ensure connection
                const connectionResult = await this.ensureConnection(taskType);
                
                if (!connectionResult.connected) {
                    if (connectionResult.loginRequired || connectionResult.noToken) {
                        throw new Error(`Authentication required: ${connectionResult.message}`);
                    }
                    throw new Error(`Connection failed: ${connectionResult.message}`);
                }

                console.log(`✅ [EPH Manager] Connection verified for ${taskType}, ready for task execution`);
                break;

            } catch (error) {
                console.error(`❌ [EPH Manager] Connection failed: ${taskType} (attempt ${attempt + 1}/${retries + 1})`, error);

                // If this is the last attempt, throw the error
                if (attempt === retries) {
                    throw error;
                }

                // Reset connection state and wait before retrying
                this.isConnected = false;
                console.log(`🔄 [EPH Manager] Waiting 1 second before retry...`);
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }

        // Run the task once, outside the retry loop: it queues a backend task,
        // and retrying after a failed response would queue duplicates.
        if (taskFunction && typeof taskFunction === 'function') {
            return await taskFunction();
        }

        return { success: true, message: 'Connection established, ready for task execution' };
    }

    /**
     * Notify extension that login is complete
     */
    notifyLoginComplete() {
        console.log('🔑 [EPH Manager] Notifying extension of login completion');
        window.dispatchEvent(new CustomEvent('eph-login-complete'));
    }

    /**
     * Check if currently connected (best guess based on last known state)
     */
    isCurrentlyConnected() {
        return this.isConnected;
    }

    /**
     * Start periodic connection check to detect stale states
     */
    startPeriodicConnectionCheck() {
        // Check every 60 seconds if connection is stale
        this.connectionCheckInterval = setInterval(() => {
            const timeSinceLastCheck = Date.now() - this.lastConnectionCheck;
            
            // If no connection check in last 5 minutes and we think we're connected, reset
            if (timeSinceLastCheck > 300000 && this.isConnected) {
                console.warn('⚠️ [EPH Manager] Connection appears stale, resetting state');
                this.isConnected = false;
                this.lastConnectionCheck = 0;
                
                // Clean up any pending promises
                this.connectionPromises.forEach((promise, taskType) => {
                    console.warn(`⚠️ [EPH Manager] Cleaning up stale promise for: ${taskType}`);
                    promise.reject(new Error('Connection check detected stale state'));
                });
                this.connectionPromises.clear();
            }
        }, 60000); // Check every 60 seconds
    }

    /**
     * Stop periodic connection check (cleanup)
     */
    stopPeriodicConnectionCheck() {
        if (this.connectionCheckInterval) {
            clearInterval(this.connectionCheckInterval);
            this.connectionCheckInterval = null;
        }
    }
}

// Global instance
const ephManager = new EPHConnectionManager();

/**
 * Convenience function for simple connection + execution
 * @param {string} taskType - Type of task
 * @param {function} taskFunction - Optional function to execute
 * @returns {Promise<any>} Task result
 */
export async function ensureConnectionAndExecute(taskType, taskFunction = null) {
    return ephManager.executeWithConnection(taskType, taskFunction);
}

/**
 * Notify extension that login is complete
 */
export function notifyLoginComplete() {
    ephManager.notifyLoginComplete();
}

/**
 * Get the global EPH connection manager instance
 */
export function getEPHManager() {
    return ephManager;
}

export { EPHConnectionManager };
export default ephManager;
